/**
 * Folders screen — list, create, rename, delete folders.
 * Entry point after login.
 */
import { getState, loadFolders, createFolder, deleteFolder, setCurrentFolder } from '../services/state';
import { folderService } from '../services/api';
import { sanitize } from '../services/utils';
import { renderWorksheetListScreen } from './list';
import { renderAdminScreen } from './admin';

let _container: HTMLElement;

export async function renderFoldersScreen(container: HTMLElement): Promise<void> {
  _container = container;
  await loadFolders();
  render();
}

function render(): void {
  const { folders } = getState();
  _container.innerHTML = `
    <div style="max-width:900px;margin:0 auto;padding:24px">
      <div style="text-align:center;margin-bottom:20px">
        <h1 style="font-size:20px;font-weight:700;color:var(--colorTextHeading);margin-bottom:4px">Use Case Discovery</h1>
        <p style="font-size:12px;color:var(--colorTextMuted)">Map how your organization uses AI — and what it takes to secure it.</p>
      </div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
        <div>
          <h2 style="font-size:16px;font-weight:600;color:var(--colorTextHeading)">Folders</h2>
          <p style="font-size:11px;color:var(--colorTextMuted)">Organize your worksheets into folders.</p>
        </div>
        <button id="new-folder-btn" style="padding:8px 16px;border-radius:8px;font-size:13px;font-weight:500;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff;display:flex;align-items:center;gap:6px">
          + New Folder
        </button>
      </div>
      <div id="folder-list" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:12px">
        ${folders.length === 0 ? renderEmptyState() : folders.map(renderFolderCard).join('')}
      </div>
    </div>
  `;

  // Event listeners
  document.getElementById('new-folder-btn')?.addEventListener('click', showCreateModal);
  document.getElementById('empty-add-btn')?.addEventListener('click', showCreateModal);
  document.getElementById('empty-create-btn')?.addEventListener('click', showCreateModal);
  document.querySelectorAll('[data-folder-id]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.folderId!;
      window.location.hash = `folder/${id}`;
    });
  });
  document.querySelectorAll('[data-delete-folder]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.deleteFolder!;
      const folder = getState().folders.find((f) => f._id === id);

      // Prevent deleting folders that contain worksheets
      if (folder?.worksheet_count > 0) {
        const btn = el as HTMLElement;
        const orig = btn.innerHTML;
        btn.innerHTML = '<span style="font-size:8px;color:var(--colorError);white-space:nowrap">Not empty</span>';
        setTimeout(() => { btn.innerHTML = orig; }, 1500);
        return;
      }

      // Replace the bottom stats row with inline confirm (same size, no layout shift)
      const card = (el as HTMLElement).closest('[data-folder-id]') as HTMLElement;
      if (!card) return;
      const statsRow = card.querySelector('[data-stats-row]') as HTMLElement;
      if (!statsRow) return;
      const originalHTML = statsRow.innerHTML;
      statsRow.innerHTML = `
        <span style="font-size:10px;color:var(--colorError);font-weight:500">Delete?</span>
        <div style="display:flex;gap:4px;margin-left:auto">
          <button data-confirm-del-folder="${id}" style="padding:2px 10px;border-radius:4px;font-size:10px;font-weight:600;cursor:pointer;border:none;background:var(--colorError);color:#fff">Delete</button>
          <button data-cancel-del-folder style="padding:2px 10px;border-radius:4px;font-size:10px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgCard);color:var(--colorText)">Cancel</button>
        </div>
      `;
      card.style.borderColor = 'var(--colorError)';
      statsRow.querySelector('[data-confirm-del-folder]')?.addEventListener('click', async (ev) => {
        (ev as Event).stopPropagation();
        await deleteFolder(id);
        render();
      });
      statsRow.querySelector('[data-cancel-del-folder]')?.addEventListener('click', (ev) => {
        (ev as Event).stopPropagation();
        statsRow.innerHTML = originalHTML;
        card.style.borderColor = '';
      });
    });
  });
}

function renderFolderCard(folder: any): string {
  return `
    <div data-folder-id="${folder._id}" style="background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:var(--radiusLg);padding:16px;cursor:pointer;transition:border-color .15s;position:relative" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <div style="display:flex;align-items:start;justify-content:space-between;margin-bottom:8px">
        <div style="display:flex;align-items:center;gap:8px">
          <div style="width:32px;height:32px;border-radius:6px;display:flex;align-items:center;justify-content:center;background:var(--colorBgElevated)">
            <svg width="16" height="16" fill="none" stroke="var(--colorAccent)" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
          </div>
          <div>
            <div style="font-size:13px;font-weight:500;color:var(--colorTextHeading)">${sanitize(folder.name)}</div>
            ${folder.ref_id ? `<div style="font-size:10px;color:var(--colorTextMuted)">${sanitize(folder.ref_id)}</div>` : ''}
          </div>
        </div>
        <button data-delete-folder="${folder._id}" style="color:var(--colorError);background:none;border:none;cursor:pointer;opacity:.4;padding:4px" title="Delete folder">
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
        </button>
      </div>
      <div data-stats-row style="display:flex;align-items:center;gap:8px;font-size:10px;color:var(--colorTextLight)">
        <span style="font-weight:500;color:var(--colorTextHeading)">${folder.worksheet_count || 0} Worksheet${folder.worksheet_count !== 1 ? 's' : ''}</span>
        <span style="margin-left:auto;font-size:10px">${folder.user_role}</span>
      </div>
    </div>
  `;
}

function renderEmptyState(): string {
  return `
    <div style="grid-column:1/-1;text-align:center;padding:60px 0">
      <div style="width:64px;height:64px;border-radius:50%;border:2px dashed var(--colorBorder);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;cursor:pointer" id="empty-add-btn">
        <svg width="24" height="24" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m6-6H6"/></svg>
      </div>
      <p style="font-size:13px;color:var(--colorTextLight);margin-bottom:12px">No folders yet.</p>
      <button id="empty-create-btn" style="padding:8px 16px;border-radius:8px;font-size:12px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorAccent)">Create your first folder</button>
    </div>
  `;
}

function showCreateModal(): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.innerHTML = `
    <h3 style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:16px">New Folder</h3>
    <form id="create-folder-form" style="display:flex;flex-direction:column;gap:12px">
      <div>
        <label style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Folder Name</label>
        <input id="folder-name-input" type="text" placeholder="e.g., Acme Corp" required autofocus
          style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
      </div>
      <div>
        <label style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Reference ID <span style="opacity:.5">(optional)</span></label>
        <input id="folder-refid-input" type="text" placeholder="Reference ID"
          style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
      </div>
      <div style="display:flex;gap:8px;margin-top:4px">
        <button type="button" onclick="this.closest('.modal-overlay').remove()" style="flex:1;padding:10px;border-radius:8px;font-size:12px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
        <button type="submit" style="flex:1;padding:10px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Create</button>
      </div>
    </form>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  document.getElementById('create-folder-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (document.getElementById('folder-name-input') as HTMLInputElement).value.trim();
    const refId = (document.getElementById('folder-refid-input') as HTMLInputElement).value.trim();
    if (!name) return;
    overlay.remove();
    await createFolder(name, refId);
    render();
  });

  setTimeout(() => document.getElementById('folder-name-input')?.focus(), 100);
}
